import { EllipsisHorizontal } from '@medusajs/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
} from '@pcrm/fe/ui-kit';
import { Fragment, type ReactNode } from 'react';

/**
 * Meniul de acțiuni al unui rând: butonul `⋯` cu grupuri separate prin linie.
 *
 * Portat din edinro (`features/settings/common/action-menu.tsx`). Rostul lui e că
 * acțiunile ies din rând: două butoane pe fiecare linie transformă un tabel de cinci
 * rânduri într-un perete de butoane, iar ochiul nu mai găsește datele.
 *
 * `DropdownMenu` e cel din shadcn (shell), `IconButton` e din Medusa (conținut) — exact
 * pe linia asta trece granița dintre cele două seturi de primitive.
 */
export interface ActionMenuAction {
  readonly icon: ReactNode;
  readonly label: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}

export interface ActionMenuGroup {
  readonly actions: readonly ActionMenuAction[];
}

export function ActionMenu({ groups }: { groups: readonly ActionMenuGroup[] }) {
  const visible = groups.filter((group) => group.actions.length > 0);

  // ⚠️ **Fără nimic de arătat, butonul nu se desenează deloc.** Altfel `⋯` apare pe fiecare
  // rând, se apasă, și nu se întâmplă nimic: un meniu gol arată identic cu unul stricat.
  if (visible.length === 0) {
    return null;
  }

  return (
    // `modal={false}`: altfel dropdown-ul blochează `pointer-events` pe `body`, iar
    // sertarul deschis dintr-un item al lui rămâne neclicabil. Capcană moștenită din edinro.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <IconButton size="small" variant="transparent" aria-label="Acțiuni">
          <EllipsisHorizontal />
        </IconButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {visible.map((group, groupIndex) => (
          // Grupurile n-au identitate proprie; sunt definite static, în ordine.
          // biome-ignore lint/suspicious/noArrayIndexKey: ordinea e cheia stabilă aici
          <Fragment key={groupIndex}>
            <DropdownMenuGroup>
              {group.actions.map((action) => (
                <DropdownMenuItem
                  key={action.label}
                  disabled={action.disabled}
                  className="flex items-center gap-x-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            {groupIndex < visible.length - 1 ? <DropdownMenuSeparator /> : null}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
